import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/auth';
import Button from '../../components/ui/Button';

const ChangeEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    const checkEmailChange = async () => {
      try {
        // Vérifier la session actuelle
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Erreur lors de la vérification de la session:', error);
          setStatus('error');
          return;
        }

        if (session?.user?.email) {
          setNewEmail(session.user.email);
          setStatus('success');
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du changement d\'email:', error);
        setStatus('error');
      }
    };

    checkEmailChange();
  }, []);

  const handleContinue = () => {
    navigate('/app');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-gray-100 flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-6 sm:py-8 px-4 sm:px-10 shadow-xl rounded-xl border border-gray-200">
          <div className="text-center">
            {status === 'loading' && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Vérification en cours...
                </h2>
                <p className="text-gray-600">
                  Nous vérifions le changement de votre adresse email.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-green-100 rounded-full">
                    <CheckCircle className="h-12 w-12 text-green-600" />
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Email changé avec succès !
                </h2>
                <div className="flex justify-center items-center mb-4">
                  <Mail className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-gray-600 text-sm">{newEmail}</span>
                </div>
                <p className="text-gray-600 mb-6">
                  Votre adresse email a été changée avec succès. Vous pouvez maintenant utiliser cette nouvelle adresse pour vous connecter.
                </p>
                <Button onClick={handleContinue} fullWidth>
                  Continuer vers le dashboard
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-red-100 rounded-full">
                    <AlertCircle className="h-12 w-12 text-red-600" />
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Erreur de changement d'email
                </h2>
                <p className="text-gray-600 mb-6">
                  Une erreur est survenue lors du changement de votre adresse email. Veuillez réessayer ou contacter le support.
                </p>
                <div className="space-y-3">
                  <Button onClick={() => navigate('/app')} fullWidth>
                    Retour au dashboard
                  </Button>
                  <Button variant="outline" onClick={() => window.location.reload()} fullWidth>
                    Réessayer
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeEmailPage;